import { useState, useEffect, useRef } from 'react';
import { 
  getAugmentationRules, 
  deleteAugmentationRule, 
  activateAugmentationRule, 
  deactivateAugmentationRule,
  cloneAugmentationRule,
  reorderAugmentationRules 
} from '../services/api/augmentationRuleApi';
import { getCustomersForDropdown } from '../services/api/customerApi';
import { getRulesetTypesByCustomer } from '../services/api/rulesetApi';

export const useAugmentationRules = (initialCustomerId = null) => {
  const [rules, setRules] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [rulesetTypes, setRulesetTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedCustomerId, setSelectedCustomerId] = useState(initialCustomerId || '');
  const [selectedRulesetTypeId, setSelectedRulesetTypeId] = useState('');
  const hasFetchedCustomers = useRef(false);

  // Fetch customers on mount
  useEffect(() => {
    if (!hasFetchedCustomers.current) {
      hasFetchedCustomers.current = true;
      fetchCustomers();
    }
  }, []);

  // Fetch rules and ruleset types when customer/ruleset changes
  useEffect(() => {
    if (selectedCustomerId) {
      fetchRules();
      fetchRulesetTypes();
    } else {
      setRules([]);
      setRulesetTypes([]);
    }
  }, [selectedCustomerId, selectedRulesetTypeId]);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const response = await getCustomersForDropdown();
      const customerList = response.data.items || response.data || [];
      setCustomers(customerList);
      
      // Auto-select first customer only if no initial customer was provided
      if (!initialCustomerId && customerList.length > 0) {
        setSelectedCustomerId(customerList[0].id);
      } else if (!initialCustomerId) {
        setLoading(false);
      }
    } catch (err) {
      setError('Failed to fetch customers');
      console.error(err);
      setLoading(false);
    }
  };

  const fetchRulesetTypes = async () => {
    if (!selectedCustomerId) return;
    
    try {
      const response = await getRulesetTypesByCustomer(selectedCustomerId);
      setRulesetTypes(response.data);
    } catch (err) {
      console.error('Failed to fetch ruleset types:', err);
      setRulesetTypes([]);
    }
  };

  const fetchRules = async () => {
    if (!selectedCustomerId) return;

    try {
      setLoading(true);
      const response = await getAugmentationRules(
        selectedCustomerId, 
        selectedRulesetTypeId || null
      );
      const sortedRules = (response.data || []).sort((a, b) => a.executionOrder - b.executionOrder);
      setRules(sortedRules);
    } catch (err) {
      setError('Failed to fetch augmentation rules');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const deleteRule = async (ruleId) => {
    try {
      await deleteAugmentationRule(ruleId);
      
      const remainingRules = rules.filter(rule => rule.id !== ruleId);
      const reorderedRules = remainingRules.map((rule, index) => ({
        ...rule,
        executionOrder: index + 1
      }));
      
      setRules(reorderedRules);
      
      const updatedRules = reorderedRules.map((rule, index) => ({
        id: rule.id,
        executionOrder: index + 1
      }));
      
      try {
        await reorderAugmentationRules(selectedCustomerId, updatedRules, selectedRulesetTypeId || null);
        setSuccess('Rule deleted successfully!');
      } catch (reorderErr) {
        console.error('Failed to update execution orders after delete:', reorderErr);
        setSuccess('Rule deleted successfully! (Refresh to see correct order)');
      }
      
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to delete rule');
      console.error(err);
      throw err;
    }
  };

  const toggleRuleActive = async (rule) => {
    try {
      if (rule.isActive) {
        await deactivateAugmentationRule(rule.id);
        setSuccess('Rule deactivated successfully!');
      } else {
        await activateAugmentationRule(rule.id);
        setSuccess('Rule activated successfully!');
      }
      
      setRules(rules.map(r => 
        r.id === rule.id ? { ...r, isActive: !r.isActive } : r
      ));
      
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(`Failed to ${rule.isActive ? 'deactivate' : 'activate'} rule`);
      console.error(err);
      throw err;
    }
  };

  const cloneRule = async (rule) => {
    try {
      const response = await cloneAugmentationRule(rule.id);
      const clonedRule = response.data;
      
      const sourceIndex = rules.findIndex(r => r.id === rule.id);
      const newRules = [...rules];
      newRules.splice(sourceIndex + 1, 0, clonedRule);
      
      const reorderedRules = newRules.map((r, index) => ({
        ...r,
        executionOrder: index + 1
      }));
      
      setRules(reorderedRules);
      
      const updatedRules = reorderedRules.map((r, index) => ({
        id: r.id,
        executionOrder: index + 1
      }));
      
      try {
        await reorderAugmentationRules(selectedCustomerId, updatedRules, selectedRulesetTypeId || null);
        setSuccess('Rule cloned successfully and inserted after the original!');
      } catch (reorderErr) {
        console.error('Failed to update execution orders after clone:', reorderErr);
        setSuccess('Rule cloned successfully! (Refresh to see correct order)');
      }
      
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to clone rule');
      console.error(err);
      throw err;
    }
  };

  const reorderRules = async (reorderedRules) => {
    // Save a snapshot of the current rules state before optimistic update
    const previousRules = [...rules];

    try {
      const updatedRules = reorderedRules.map((rule, index) => ({
        id: rule.id,
        executionOrder: index + 1
      }));

      const newRules = reorderedRules.map((rule, index) => ({
        ...rule,
        executionOrder: index + 1
      }));
      
      setRules(newRules);

      await reorderAugmentationRules(selectedCustomerId, updatedRules, selectedRulesetTypeId || null);
      setSuccess('Rules reordered successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error(err);

      // Revert to the pre-reorder snapshot
      setRules(previousRules);

      if (err.response?.status === 400 && err.response?.data?.errors) {
        // Display the dependency error message from the API response
        const errors = err.response.data.errors;
        const errorMessage = errors.map(e => e.message).join('; ');
        setError(errorMessage || 'Reorder rejected: a rule dependency would be broken.');
      } else {
        setError('Failed to reorder rules.');
      }

      setTimeout(() => setError(null), 5000);
      throw err;
    }
  };

  return {
    rules,
    customers,
    rulesetTypes,
    loading,
    error,
    success,
    selectedCustomerId,
    selectedRulesetTypeId,
    setSelectedCustomerId,
    setSelectedRulesetTypeId,
    fetchRules,
    deleteRule,
    toggleRuleActive,
    cloneRule,
    reorderRules,
  };
};
